import { useEffect, useState } from "react";

import { apiGet } from "@/utils/apiClient";
import { API_ENDPOINTS } from "@/constants/api";

interface BrandMetadataOption {
  value: string;
  label: string;
}

interface BrandMetadataResponse {
  brand_types: BrandMetadataOption[];
  industries: BrandMetadataOption[];
  primary_languages: BrandMetadataOption[];
}

interface BrandMetadata {
  brandTypes: readonly BrandMetadataOption[];
  industries: readonly BrandMetadataOption[];
  primaryLanguages: readonly BrandMetadataOption[];
}

const EMPTY: BrandMetadata = {
  brandTypes: [],
  industries: [],
  primaryLanguages: [],
};

let cachedMetadata: BrandMetadata | null = null;

/**
 * Fetch brand-metadata options from the backend (DB-backed) and cache them
 * for the lifetime of the SPA.  Returns empty arrays while loading.
 */
export function useBrandMetadata(): BrandMetadata {
  const [metadata, setMetadata] = useState<BrandMetadata>(
    cachedMetadata ?? EMPTY,
  );

  useEffect(() => {
    if (cachedMetadata) return; // already fetched

    let cancelled = false;

    apiGet<BrandMetadataResponse>(API_ENDPOINTS.brandMetadata, {
      auth: "tokenApi",
    })
      .then((data) => {
        if (cancelled) return;
        const result: BrandMetadata = {
          brandTypes: data.brand_types,
          industries: data.industries,
          primaryLanguages: data.primary_languages,
        };
        cachedMetadata = result;
        setMetadata(result);
      })
      .catch(() => {
        // API unreachable — selects stay empty
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return metadata;
}

export type { BrandMetadataOption };
